import { useEffect, useRef, useCallback, useState } from 'react'

// Load Joy-Con module dynamically
async function loadJoyConModule() {
  try {
    const JoyConModule = await import('../lib/index.js')
    console.log('🎮 Joy-Con module loaded successfully')
    return JoyConModule
  } catch (error) {
    console.warn('🎮 Joy-Con module not available:', error.message)
    return null
  }
}

export const useJoyConManager = () => {
  const [joyConState, setJoyConState] = useState({
    enabled: false,
    connected: false,
    intensity: 0.6,
    isScanning: false,
    status: '❌ Aucun Joy-Con connecté',
    info: 'Connectez vos Joy-Con via Bluetooth puis cliquez sur "Connecter Joy-Con"'
  })

  const joyConModuleRef = useRef(null)
  const connectedJoyConsRef = useRef(new Map())
  const scanIntervalRef = useRef(null)

  // Initialize Joy-Con controls
  const initJoyConControls = useCallback(async () => {
    // Check if WebHID is supported
    if (!navigator.hid) {
      setJoyConState(prev => ({
        ...prev,
        info: '❌ WebHID non supporté dans ce navigateur'
      }))
      return false
    }

    // Try to load Joy-Con module
    const moduleLoaded = await loadJoyConModule()
    if (!moduleLoaded) {
      setJoyConState(prev => ({
        ...prev,
        info: '⚠️ Module Joy-Con non disponible'
      }))
      return false
    }

    joyConModuleRef.current = moduleLoaded

    // Start scanning for Joy-Cons
    scanIntervalRef.current = setInterval(scanForJoyCons, 2000)

    console.log('🎮 Joy-Con controls initialized')
    return true
  }, [])

  // Connect Joy-Cons
  const connectJoyCons = useCallback(async () => {
    if (!joyConModuleRef.current) return

    try {
      setJoyConState(prev => ({
        ...prev,
        info: '🔗 Connexion en cours...'
      }))

      await joyConModuleRef.current.connectJoyCon()
      
      setJoyConState(prev => ({
        ...prev,
        info: '✅ Demande de connexion envoyée'
      }))
    } catch (error) {
      console.error('Joy-Con connection error:', error)
      setJoyConState(prev => ({
        ...prev,
        info: `❌ Erreur: ${error.message}`
      }))
    }
  }, [])

  // Scan for connected Joy-Cons
  const scanForJoyCons = useCallback(async () => {
    if (!joyConModuleRef.current || joyConState.isScanning) return

    setJoyConState(prev => ({ ...prev, isScanning: true }))

    try {
      for (const joyCon of joyConModuleRef.current.connectedJoyCons.values()) {
        const joyConId = joyCon.device.vendorId + '-' + joyCon.device.productId + '-' + joyCon.device.serialNumber

        if (joyCon.eventListenerAttached) {
          continue
        }

        console.log(`🔧 Configuring Joy-Con: ${joyCon.device.productName}`)

        try {
          await joyCon.open()
          await joyCon.enableStandardFullMode()
          await joyCon.enableIMUMode()
          await joyCon.enableVibration()

          const deviceInfo = await joyCon.getDeviceInfo()
          connectedJoyConsRef.current.set(joyConId, {
            joyCon,
            ready: true,
            deviceInfo
          })

          console.log(`✅ Joy-Con configured: ${joyCon.device.productName}`)

          // Test vibration on connect
          await joyCon.rumble(400, 400, 0.5)
          setTimeout(async () => {
            await joyCon.rumble(0, 0, 0)
          }, 200)

          joyCon.eventListenerAttached = true

        } catch (error) {
          console.error(`Joy-Con configuration error: ${error.message}`)
          connectedJoyConsRef.current.set(joyConId, {
            joyCon,
            ready: false,
            error: error.message
          })
        }
      }

      updateJoyConStatus()

    } catch (error) {
      console.error('Joy-Con scan error:', error)
    } finally {
      setJoyConState(prev => ({ ...prev, isScanning: false }))
    }
  }, [joyConState.isScanning])

  // Update Joy-Con status display
  const updateJoyConStatus = useCallback(() => {
    if (!joyConModuleRef.current) return

    const connectedCount = joyConModuleRef.current.connectedJoyCons.size
    const readyCount = Array.from(connectedJoyConsRef.current.values())
      .filter(info => info.ready).length

    if (connectedCount === 0) {
      setJoyConState(prev => ({
        ...prev,
        connected: false,
        status: '❌ Aucun Joy-Con connecté',
        info: 'Connectez vos Joy-Con via Bluetooth puis cliquez sur "Connecter Joy-Con"'
      }))
    } else {
      // Build info text with connected controllers
      let infoText = ''
      for (const joyCon of joyConModuleRef.current.connectedJoyCons.values()) {
        const name = joyCon.device.productName || 'Joy-Con'
        infoText += `• ${name}\n`
      }

      setJoyConState(prev => ({
        ...prev,
        connected: true,
        status: `✅ ${connectedCount} Joy-Con(s) connecté(s) (${readyCount} prêt(s))`,
        info: infoText || 'Joy-Cons connectés et configurés'
      }))
    }
  }, [])

  // Trigger Joy-Con vibration synchronized with audio
  const triggerJoyConVibration = useCallback(async (side, soundDuration = 150) => {
    if (!joyConModuleRef.current || !joyConState.connected || !joyConState.enabled) return

    try {
      const intensity = joyConState.intensity
      const duration = soundDuration

      // Convert intensity (0-1) to frequency range (160-600Hz)
      const baseFreq = 160 + (intensity * 440)

      for (const joyCon of joyConModuleRef.current.connectedJoyCons.values()) {
        // Check if this Joy-Con matches the desired side
        const isLeftJoyCon = joyCon.device.productName.includes('Left') || joyCon.device.productName.includes('(L)')
        const isRightJoyCon = joyCon.device.productName.includes('Right') || joyCon.device.productName.includes('(R)')

        // Only vibrate the Joy-Con that corresponds to the audio side
        if ((side === 'left' && isLeftJoyCon) || (side === 'right' && isRightJoyCon)) {
          await joyCon.rumble(baseFreq, baseFreq, intensity)

          // Stop vibration after duration
          setTimeout(async () => {
            try {
              await joyCon.rumble(0, 0, 0)
            } catch (e) {
              console.warn('Failed to stop Joy-Con vibration:', e.message)
            }
          }, duration)
        }
      }
    } catch (error) {
      console.warn('Joy-Con vibration error:', error.message)
    }
  }, [joyConState.connected, joyConState.enabled, joyConState.intensity])

  // Update Joy-Con settings
  const updateJoyConEnabled = useCallback((enabled) => {
    setJoyConState(prev => ({
      ...prev,
      enabled,
      info: enabled ? '✅ Stimulation haptique activée' : '⏸️ Stimulation haptique désactivée'
    }))
  }, [])

  const updateJoyConIntensity = useCallback((intensity) => {
    setJoyConState(prev => ({ ...prev, intensity }))
  }, [])

  // Initialize on mount
  useEffect(() => {
    initJoyConControls()

    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current)
      }
    }
  }, [initJoyConControls])

  return {
    joyConState,
    connectJoyCons,
    triggerJoyConVibration,
    updateJoyConEnabled,
    updateJoyConIntensity
  }
} 